import { useState, type ChangeEvent, type FormEvent } from "react"
import { useForm } from "@inertiajs/react"
import { Eye, EyeOff } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FieldError, errorMessage } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { t } from "@/lib/i18n"

/**
 * Who the restaurant is to the SAT, and how Menny reaches its stamping provider.
 *
 * **An explicit submit, and deliberately not an inline update.** Everything else
 * on the manage screens commits per field with `InlineInput` — a price, a phone
 * number, a rename. This does not, and the rule that says so is
 * `.claude/rules/system-design.md`'s "a setup screen that configures a critical
 * resource in one transaction keeps a submit button even when the verb is PATCH".
 * Two things make this that case. The RFC and the regime are one decision: a
 * regime committed on blur, against an RFC still half-typed, describes a
 * taxpayer who does not exist. And an RFC wrong by one character is not a field
 * that reads oddly — it is every invoice this restaurant issues being refused,
 * or worse, accepted against somebody else's taxpayer.
 *
 * **The PAC key is write-only and the form says so out loud.** It never arrives
 * in the props (`Staff::Location::Manage::TaxProfileController#tax_profile_props`
 * has no field for it, and a request spec asserts it never reaches the body), so
 * the input starts empty on every visit. Empty means "leave it alone" — the
 * server drops a blank one — because otherwise a manager fixing the postal code
 * would silently erase the credential and stop the restaurant invoicing without
 * touching anything that looked like it mattered.
 */

export type TaxProfile = {
  /** A row exists. False on a restaurant that has just been granted the feature. */
  configured: boolean
  /** A PAC credential is stored. Nothing stamps without one. */
  pacConfigured: boolean
  rfc: string | null
  legalName: string | null
  taxRegimeCode: string | null
  postalCode: string | null
  defaultSeries: string | null
  defaultProductCode: string | null
  defaultUnitCode: string | null
  pacProvider: string
  pacEnvironment: string
}

export type CatalogOption = { value: string; label: string }

const tProfile = (key: string) =>
  t(`ui.react.pages.staff.location.manage.tax_profile.show.${key}`)

type FiscalDataFormProps = {
  taxProfile: TaxProfile
  taxRegimeOptions: CatalogOption[]
  pacEnvironmentOptions: CatalogOption[]
  errors: Record<string, string | string[]>
}

export function FiscalDataForm({
  taxProfile,
  taxRegimeOptions,
  pacEnvironmentOptions,
  errors,
}: FiscalDataFormProps) {
  const { data, setData, patch, processing } = useForm({
    tax_profile: {
      rfc: taxProfile.rfc ?? "",
      legal_name: taxProfile.legalName ?? "",
      tax_regime_code: taxProfile.taxRegimeCode ?? "",
      postal_code: taxProfile.postalCode ?? "",
      default_series: taxProfile.defaultSeries ?? "",
      default_product_code: taxProfile.defaultProductCode ?? "",
      default_unit_code: taxProfile.defaultUnitCode ?? "",
      pac_environment: taxProfile.pacEnvironment,
      // Never seeded from the server: there is nothing to seed it with.
      pac_api_key: "",
    },
  })
  // Typed once and then hidden, like every other credential field. It is not
  // state the server knows about, so it lives here rather than in the form data.
  const [revealKey, setRevealKey] = useState(false)

  const setField = (field: keyof typeof data.tax_profile, value: string) => {
    setData("tax_profile", { ...data.tax_profile, [field]: value })
  }

  const handleInput = (field: keyof typeof data.tax_profile) => (event: ChangeEvent<HTMLInputElement>) =>
    setField(field, event.target.value)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    patch("/manage/tax_profile", { preserveScroll: true })
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="tax-profile-form">
      <Card data-testid="tax-profile-identity-card">
        <CardHeader>
          <CardTitle>{tProfile("identity_title")}</CardTitle>
          <CardDescription>{tProfile("identity_description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="tax-profile-rfc">{tProfile("rfc_label")}</Label>
            <Input
              id="tax-profile-rfc"
              value={data.tax_profile.rfc}
              onChange={handleInput("rfc")}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={Boolean(errorMessage(errors.rfc)) || undefined}
              aria-describedby="tax-profile-rfc-hint"
              data-testid="tax-profile-rfc"
            />
            <p id="tax-profile-rfc-hint" className="text-xs text-muted-foreground">
              {tProfile("rfc_hint")}
            </p>
            <FieldError error={errors.rfc} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-legal-name">{tProfile("legal_name_label")}</Label>
            <Input
              id="tax-profile-legal-name"
              value={data.tax_profile.legal_name}
              onChange={handleInput("legal_name")}
              aria-invalid={Boolean(errorMessage(errors.legal_name)) || undefined}
              aria-describedby="tax-profile-legal-name-hint"
              data-testid="tax-profile-legal-name"
            />
            <p id="tax-profile-legal-name-hint" className="text-xs text-muted-foreground">
              {tProfile("legal_name_hint")}
            </p>
            <FieldError error={errors.legal_name} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-tax-regime-code">{tProfile("tax_regime_label")}</Label>
            {/* The catalog comes from the server (Cfdi::TaxRegimeCatalog) so this
                select cannot offer a code the model would then refuse. */}
            <Select
              value={data.tax_profile.tax_regime_code}
              onValueChange={(value) => setField("tax_regime_code", value)}
            >
              <SelectTrigger
                id="tax-profile-tax-regime-code"
                className="w-full"
                aria-invalid={Boolean(errorMessage(errors.tax_regime_code)) || undefined}
                data-testid="tax-profile-tax-regime-code"
              >
                <SelectValue placeholder={tProfile("tax_regime_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {taxRegimeOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    data-testid={`tax-profile-tax-regime-code-option-${option.value}`}
                  >
                    {option.value} · {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError error={errors.tax_regime_code} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-postal-code">{tProfile("postal_code_label")}</Label>
            <Input
              id="tax-profile-postal-code"
              inputMode="numeric"
              value={data.tax_profile.postal_code}
              onChange={handleInput("postal_code")}
              className="tabular-nums"
              aria-invalid={Boolean(errorMessage(errors.postal_code)) || undefined}
              aria-describedby="tax-profile-postal-code-hint"
              data-testid="tax-profile-postal-code"
            />
            <p id="tax-profile-postal-code-hint" className="text-xs text-muted-foreground">
              {tProfile("postal_code_hint")}
            </p>
            <FieldError error={errors.postal_code} />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="tax-profile-pac-card">
        <CardHeader>
          <CardTitle>{tProfile("pac_title")}</CardTitle>
          <CardDescription>{tProfile("pac_description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stated, not picked: `simulated` is the only provider the server
              offers, and a select with one option is a control that does
              nothing. It becomes a Select when there is a second one. */}
          <div className="space-y-2">
            <Label>{tProfile("pac_provider_label")}</Label>
            <p className="text-sm font-medium" data-testid="tax-profile-pac-provider">
              {taxProfile.pacProvider}
            </p>
            <p className="text-xs text-muted-foreground">{tProfile("pac_provider_note")}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-pac-environment">{tProfile("pac_environment_label")}</Label>
            <Select
              value={data.tax_profile.pac_environment}
              onValueChange={(value) => setField("pac_environment", value)}
            >
              <SelectTrigger
                id="tax-profile-pac-environment"
                className="w-full"
                data-testid="tax-profile-pac-environment"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pacEnvironmentOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    data-testid={`tax-profile-pac-environment-option-${option.value}`}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{tProfile("pac_environment_hint")}</p>
            <FieldError error={errors.pac_environment} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-pac-api-key">{tProfile("pac_key_label")}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="tax-profile-pac-api-key"
                type={revealKey ? "text" : "password"}
                value={data.tax_profile.pac_api_key}
                onChange={handleInput("pac_api_key")}
                autoComplete="off"
                spellCheck={false}
                placeholder={
                  taxProfile.pacConfigured
                    ? tProfile("pac_key_placeholder_configured")
                    : tProfile("pac_key_placeholder_empty")
                }
                aria-invalid={Boolean(errorMessage(errors.pac_api_key)) || undefined}
                aria-describedby="tax-profile-pac-api-key-hint"
                data-testid="tax-profile-pac-api-key"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-pressed={revealKey}
                aria-label={tProfile("pac_key_label")}
                onClick={() => setRevealKey((previous) => !previous)}
                data-testid="tax-profile-pac-api-key-reveal"
              >
                {revealKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
            <p id="tax-profile-pac-api-key-hint" className="text-xs text-muted-foreground">
              {tProfile("pac_key_hint")}
            </p>
            {/* The dot rule: colour lives in the mark, and the sentence beside it
                carries the same meaning without colour vision. */}
            <p className="flex items-center gap-1.5 text-xs" data-testid="tax-profile-pac-key-state">
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-full ${
                  taxProfile.pacConfigured ? "bg-success" : "bg-warning"
                }`}
              />
              <span className="text-muted-foreground">
                {taxProfile.pacConfigured ? tProfile("pac_key_configured") : tProfile("pac_key_missing")}
              </span>
            </p>
            <FieldError error={errors.pac_api_key} />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="tax-profile-defaults-card">
        <CardHeader>
          <CardTitle>{tProfile("defaults_title")}</CardTitle>
          <CardDescription>{tProfile("defaults_description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="tax-profile-default-series">{tProfile("series_label")}</Label>
            <Input
              id="tax-profile-default-series"
              value={data.tax_profile.default_series}
              onChange={handleInput("default_series")}
              data-testid="tax-profile-default-series"
            />
            <p className="text-xs text-muted-foreground">{tProfile("series_hint")}</p>
            <FieldError error={errors.default_series} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-default-product-code">{tProfile("product_code_label")}</Label>
            <Input
              id="tax-profile-default-product-code"
              inputMode="numeric"
              value={data.tax_profile.default_product_code}
              onChange={handleInput("default_product_code")}
              className="tabular-nums"
              data-testid="tax-profile-default-product-code"
            />
            <p className="text-xs text-muted-foreground">{tProfile("product_code_hint")}</p>
            <FieldError error={errors.default_product_code} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax-profile-default-unit-code">{tProfile("unit_code_label")}</Label>
            <Input
              id="tax-profile-default-unit-code"
              value={data.tax_profile.default_unit_code}
              onChange={handleInput("default_unit_code")}
              data-testid="tax-profile-default-unit-code"
            />
            <p className="text-xs text-muted-foreground">{tProfile("unit_code_hint")}</p>
            <FieldError error={errors.default_unit_code} />
          </div>
        </CardContent>
      </Card>

      <Button type="submit" disabled={processing} data-testid="tax-profile-submit">
        {processing ? tProfile("submitting") : tProfile("submit")}
      </Button>
    </form>
  )
}
